import { APIProvider, Map, Marker, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { SessionBusinessArea } from 'model/ServiceSession';
import React from 'react';
import { ZoneCircle } from './ZoneCircle';
import {
    DEFAULT_MAP_CENTER,
    DEFAULT_MAP_ZOOM,
    milesToMeters,
    pickGeocodeAddress,
    SELECTED_LOCATION_FALLBACK
} from './zoneMapConfig';

export interface ZoneMapProps {
    area: SessionBusinessArea;
    latitude?: number;
    longitude?: number;
    radiusMiles: number;
    interactive: boolean;
    resolveAddress?: boolean;
    onResolvedAddress?: (address: string) => void;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            flex: 1,
            minHeight: 0,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 8,
            border: 'solid 1px #D4D4D4',
            backgroundColor: '#FAFAFA'
        },
        overlay: {
            inset: 0,
            zIndex: 1,
            position: 'absolute',
            backgroundColor: 'rgba(255, 255, 255, 0.35)'
        },
        missingKey: {
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center'
        }
    })
);

const MapViewport: React.FC<{
    interactive: boolean;
    latitude?: number;
    longitude?: number;
    radiusMiles: number;
}> = ({ interactive, latitude, longitude, radiusMiles }) => {
    const map = useMap();

    React.useEffect(() => {
        if (!map) return;

        map.setOptions({
            gestureHandling: interactive ? 'greedy' : 'none',
            zoomControl: interactive,
            keyboardShortcuts: interactive
        });

        if (!interactive || latitude === undefined || longitude === undefined) {
            map.setCenter(DEFAULT_MAP_CENTER);
            map.setZoom(DEFAULT_MAP_ZOOM);
            return;
        }

        const circle = new google.maps.Circle({
            center: { lat: latitude, lng: longitude },
            radius: milesToMeters(radiusMiles)
        });
        const bounds = circle.getBounds();
        if (bounds) map.fitBounds(bounds, 48);
    }, [map, interactive, latitude, longitude, radiusMiles]);

    return null;
};

const ReverseGeocode: React.FC<{
    latitude: number;
    longitude: number;
    enabled: boolean;
    onResolvedAddress?: (address: string) => void;
}> = ({ latitude, longitude, enabled, onResolvedAddress }) => {
    const mapsLoaded = useApiIsLoaded();

    React.useEffect(() => {
        if (!mapsLoaded || !enabled || !onResolvedAddress) return;

        let cancelled = false;
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (cancelled) return;

            onResolvedAddress(
                status === 'OK' ? pickGeocodeAddress(results) || SELECTED_LOCATION_FALLBACK : SELECTED_LOCATION_FALLBACK
            );
        });

        return () => {
            cancelled = true;
        };
    }, [mapsLoaded, enabled, latitude, longitude, onResolvedAddress]);

    return null;
};

export const ZoneMap: React.FC<ZoneMapProps> = props => {
    const classes = useStyles();
    const hasLocation = props.latitude !== undefined && props.longitude !== undefined;

    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <div
                className={classes.root}
                id={`${props.area}-zone-map`}
            >
                <Typography className={classes.missingKey}>Google Maps is not configured.</Typography>
            </div>
        );
    }

    return (
        <div
            className={classes.root}
            id={`${props.area}-zone-map`}
            data-interactive={props.interactive}
        >
            <Map
                defaultCenter={DEFAULT_MAP_CENTER}
                defaultZoom={DEFAULT_MAP_ZOOM}
                gestureHandling={props.interactive ? 'greedy' : 'none'}
                disableDefaultUI
                zoomControl={props.interactive}
                clickableIcons={false}
                keyboardShortcuts={props.interactive}
            >
                <MapViewport
                    interactive={props.interactive}
                    latitude={props.latitude}
                    longitude={props.longitude}
                    radiusMiles={props.radiusMiles}
                />

                {hasLocation && (
                    <>
                        <Marker
                            position={{ lat: props.latitude!, lng: props.longitude! }}
                            clickable={false}
                        />
                        <ZoneCircle
                            latitude={props.latitude!}
                            longitude={props.longitude!}
                            radiusMiles={props.radiusMiles}
                        />
                    </>
                )}
            </Map>

            {hasLocation && (
                <ReverseGeocode
                    latitude={props.latitude!}
                    longitude={props.longitude!}
                    enabled={!!props.resolveAddress}
                    onResolvedAddress={props.onResolvedAddress}
                />
            )}

            {!props.interactive && <div className={classes.overlay} />}
        </div>
    );
};

export const ZoneMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (!GOOGLE_MAPS_API_KEY) return <>{children}</>;

    return (
        <APIProvider
            apiKey={GOOGLE_MAPS_API_KEY}
            libraries={['places']}
        >
            {children}
        </APIProvider>
    );
};
